// ── Explorer Ranks + XP mapping (Command 2) ─────────────────────────
// XP already exists in progress.js as `stars` (accumulating, never
// decreases — perfect match for the command). We layer FIVE named
// ranks over the total-stars pool.
//
// Rank thresholds are chosen so a genuinely engaged child rises through
// them naturally without grinding — Beginner from day 1, Junior within
// the first serious week, Global Quest Champion is a long-term goal.

import { loadProgress } from "@/lib/progress";
import { getRecentRuns } from "@/lib/timedRuns";
import {
  RANK_TIERS, MIN_RUNS_FOR_RANK, SPEED_FAST_MS, SPEED_SLOW_MS,
  RANK_WEIGHTS, RELIABILITY_BAR,
} from "@/lib/explorerRankConfig";

export const EXPLORER_RANKS = [
  { id: "beginner",  label: "Explorer Beginner",      min: 0,    emoji: "🧭" },
  { id: "junior",    label: "Junior Explorer",        min: 25,   emoji: "🎒" },
  { id: "world",     label: "World Explorer",         min: 75,   emoji: "🌍" },
  { id: "master",    label: "Master Explorer",        min: 200,  emoji: "🏔️" },
  { id: "champion",  label: "Global Quest Champion",  min: 500,  emoji: "🏆" },
];

/** Current rank for the given XP total. Returns the highest rank whose
 *  `min` is <= xp. */
export function rankForXp(xp) {
  const total = Number(xp) || 0;
  let out = EXPLORER_RANKS[0];
  for (const r of EXPLORER_RANKS) if (total >= r.min) out = r;
  return out;
}

/** The next rank after the given XP, or null if the child is already
 *  at the top rank. */
export function nextRank(xp) {
  const total = Number(xp) || 0;
  return EXPLORER_RANKS.find((r) => r.min > total) || null;
}

/** How many XP away from the next rank. Null when at top. */
export function xpToNext(xp) {
  const nxt = nextRank(xp);
  if (!nxt) return null;
  return Math.max(0, nxt.min - (Number(xp) || 0));
}

/** Full snapshot for the top-right rank badge. */
export function rankSnapshot() {
  const p = loadProgress();
  const xp = Number(p.stars) || 0;
  const rank = rankForXp(xp);
  const nxt = nextRank(xp);
  return { xp, rank, next: nxt, xpToNext: nxt ? Math.max(0, nxt.min - xp) : 0 };
}

// ── Themed Badges (Command 2 · Badges) ──────────────────────────────
// Each badge is a *named collection* of already-existing rewards
// (country stamps, subject mastery counts, arc badges). We evaluate
// eligibility from the same progress state — no separate content.

export const THEMED_BADGES = [
  { id: "badge-geo-genius",       label: "Geography Genius",   emoji: "🗺️", need: 15, kind: "countries", hint: "Learn 15 countries." },
  { id: "badge-ocean-explorer",   label: "Ocean Explorer",     emoji: "🐬", need: 8,  kind: "ocean-countries", hint: "Visit 8 ocean-facing countries." },
  { id: "badge-maths-master",     label: "Maths Master",       emoji: "🧮", need: 25, kind: "quiz-correct", hint: "Answer 25 maths questions right." },
  { id: "badge-story-explorer",   label: "Story Explorer",     emoji: "📚", need: 5,  kind: "arcs-complete", hint: "Finish 5 story arcs." },
  { id: "badge-dinosaur",         label: "Dinosaur Detective", emoji: "🦖", need: 30, kind: "activities", hint: "Complete 30 activities." },
  { id: "badge-young-scientist",  label: "Young Scientist",    emoji: "🔬", need: 10, kind: "activities", hint: "Complete 10 activities." },
  { id: "badge-volcano-expert",   label: "Volcano Expert",     emoji: "🌋", need: 3,  kind: "specific-countries", codes: ["is", "id", "it", "jp", "us"], hint: "Visit 3 volcano countries." },
];

// Very small set of ocean-facing country ISO codes (a helper subset —
// intentionally short so this file stays lightweight). Full list can
// grow later; near-miss framing still works because the count is what
// matters.
const OCEAN_COUNTRIES = new Set(["au", "nz", "jp", "us", "br", "ca", "gb", "fr", "za", "in", "id", "ph", "gr", "es", "no", "no", "ru", "cn", "kr", "th", "vn"]);

/** Count how far the child is toward each badge. Returns array of
 *  `{ badge, have, need, ratio, earned }`. Zero side effects. */
export function badgeProgress() {
  const p = loadProgress();
  const countries = new Set(p.countriesLearned || []);
  const activities = (p.activity || []).length;
  const quizCorrect = Number(p.quizCorrect || 0);
  let arcsDone = 0;
  try { arcsDone = (JSON.parse(localStorage.getItem("we_story_arcs_v1") || "{}").completedArcs || []).length; } catch { /* ignore */ }
  const badgesEarned = new Set((p.badges || []).map((b) => b.id));

  return THEMED_BADGES.map((b) => {
    let have = 0;
    if (b.kind === "countries")           have = countries.size;
    if (b.kind === "activities")          have = activities;
    if (b.kind === "quiz-correct")        have = quizCorrect;
    if (b.kind === "arcs-complete")       have = arcsDone;
    if (b.kind === "ocean-countries")     have = [...countries].filter((c) => OCEAN_COUNTRIES.has(c)).length;
    if (b.kind === "specific-countries")  have = (b.codes || []).filter((c) => countries.has(c)).length;
    const earned = badgesEarned.has(b.id) || have >= b.need;
    return {
      badge: b,
      have: Math.min(have, b.need),
      need: b.need,
      ratio: Math.min(1, have / b.need),
      earned,
      remaining: Math.max(0, b.need - have),
    };
  });
}

/** The single "closest" un-earned badge — used by the "2 more to earn
 *  Ocean Explorer" near-miss framing on the rank badge popover. */
export function nextBadgeNearMiss() {
  const rows = badgeProgress().filter((r) => !r.earned && r.have > 0);
  rows.sort((a, b) => a.remaining - b.remaining);
  return rows[0] || null;
}

// ══════════════════════════════════════════════════════════════════
// PHASE 6 · Per-subject Explorer Rank from the last 10 eligible timed
// runs. This is the new headline performance measure. It replaces the
// XP-based rank in the CHILD UI (the XP helpers above are retained only
// for the adult Teacher Report / legacy callers).
// ══════════════════════════════════════════════════════════════════
function mean(xs) {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}
function stdev(xs) {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(mean(xs.map((x) => (x - m) ** 2)));
}
function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}
function speedFactor(ms) {
  // 1 = at/under FAST, 0 = at/over SLOW.
  return clamp01((SPEED_SLOW_MS - ms) / (SPEED_SLOW_MS - SPEED_FAST_MS));
}
function tierForScore(score) {
  let out = RANK_TIERS[0];
  for (const t of RANK_TIERS) if (score >= t.min) out = t;
  return out;
}
function nextTierForScore(score) {
  return RANK_TIERS.find((t) => t.min > score) || null;
}

/**
 * Compute the Explorer Rank for one subject for the ACTIVE learner.
 * Returns a fully-explained snapshot. Never fabricates a rank from
 * fewer than MIN_RUNS_FOR_RANK eligible runs.
 */
export function computeSubjectRank(subject) {
  const runs = getRecentRuns(subject, MIN_RUNS_FOR_RANK); // newest → oldest
  const n = runs.length;
  const newExplorer = RANK_TIERS[0];

  if (n < MIN_RUNS_FOR_RANK) {
    return {
      subject,
      isNew: true,
      runsCompleted: n,
      runsNeeded: MIN_RUNS_FOR_RANK,
      score: null,
      tier: newExplorer,
      next: RANK_TIERS[1],
      pointsToNext: null,
      lever: null,
      progressLabel: `${n} of ${MIN_RUNS_FOR_RANK} timed runs completed`,
      avgAccuracy: n ? mean(runs.map((r) => r.accuracy)) : 0,
      avgResponseMs: n ? mean(runs.map((r) => r.avgResponseMs)) : 0,
    };
  }

  const accs = runs.map((r) => r.accuracy);
  const accMean = mean(accs);
  const reliability = mean(accs.map((a) => (a >= RELIABILITY_BAR ? 1 : 0)));
  // Speed credited only in proportion to that run's accuracy.
  const speedCredit = mean(runs.map((r) => speedFactor(r.avgResponseMs) * r.accuracy));
  const consistency = clamp01(1 - stdev(accs) / 0.5);

  const score = Math.round(
    accMean * RANK_WEIGHTS.accuracy +
    reliability * RANK_WEIGHTS.reliability +
    speedCredit * RANK_WEIGHTS.speed +
    consistency * RANK_WEIGHTS.consistency
  );

  const tier = tierForScore(score);
  const next = nextTierForScore(score);
  const avgResponseMs = mean(runs.map((r) => r.avgResponseMs));

  let lever = null;
  let progressLabel = `${score} points`;
  if (next) {
    const pointsToNext = next.min - score;
    // Speed is the true remaining lever only when accuracy is essentially
    // maxed (so more points can't come from accuracy/reliability) AND
    // there is genuine speed headroom.
    const accuracyMaxed = accMean >= 0.98 && reliability >= 0.99;
    const speedHeadroom = speedCredit < 0.99;
    if (accuracyMaxed && speedHeadroom) {
      lever = "speed";
      // Invert the score model to find the target average time. Only the
      // speed term can move; solve for the required speedFactor.
      const targetSpeedCredit = clamp01(speedCredit + pointsToNext / RANK_WEIGHTS.speed);
      const targetMs = SPEED_SLOW_MS - targetSpeedCredit * (SPEED_SLOW_MS - SPEED_FAST_MS);
      const secsFaster = Math.max(0.1, (avgResponseMs - targetMs) / 1000);
      progressLabel = `${(avgResponseMs / 1000).toFixed(1)}s average · ${secsFaster.toFixed(1)}s faster to reach ${next.label}`;
    } else {
      lever = "points";
      const plural = pointsToNext === 1 ? "point" : "points";
      progressLabel = `${score} points · ${pointsToNext} ${plural} to ${next.label}`;
    }
    return {
      subject, isNew: false, runsCompleted: n, runsNeeded: MIN_RUNS_FOR_RANK,
      score, tier, next, pointsToNext, lever, progressLabel,
      avgAccuracy: accMean, avgResponseMs,
    };
  }

  return {
    subject, isNew: false, runsCompleted: n, runsNeeded: MIN_RUNS_FOR_RANK,
    score, tier, next: null, pointsToNext: null, lever: null,
    progressLabel: `${score} points · Top rank!`,
    avgAccuracy: accMean, avgResponseMs,
  };
}

